/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { ThemeProvider, createGlobalStyle }  from 'styled-components';
import { Provider } from 'react-redux';
import get from 'lodash/get';
import { Helmet } from 'react-helmet';
import Layout from './layout';
import { history } from './redux/configureStore';
import initApp from './initApp';
import { resizeListener } from './utils';
import HealthCheck from './healthCheck';
import IdentityCheck from './identityCheck';
import { getNextQuestionSet } from './redux/actions';
import { getParams } from './util';
import FullStoryInitialization from './fullstory';

const GlobalStyle = createGlobalStyle`
  body {
	font-family: ${(props) => props.fontFamily };
  }
`
const Main = ({ config, store, ssoAuthConfig }) => {

	const _resizeListener = useCallback(resizeListener.bind(window, store), []);
	const fontFamily = get(config, 'theme.global.fontFamily', '');
	const identityCheckEnable = get(config, 'features.identityCheck.enable', false);
	const identityCheckTimeoutMs = get(config, 'features.identityCheck.timeoutMs', 10000);
	// Track identity so POP/back does not fire /questions on a blocked tab.
	const identityStatusRef = useRef(identityCheckEnable ? 'checking' : 'allowed');
	const initAppStartedRef = useRef(false);

	// Run initApp only once, and only after identity allow (or feature off).
	const runInitApp = useCallback(() => {
		if (initAppStartedRef.current) {
			return;
		}
		initAppStartedRef.current = true;
		initApp(store, config, ssoAuthConfig);
	}, [store, config, ssoAuthConfig]);

	const handleIdentityStatus = useCallback((status) => {
		identityStatusRef.current = status;
		if (status === 'allowed') {
			runInitApp();
		}
	}, [runInitApp]);

	useEffect(() => {
		window.addEventListener('resize', _resizeListener);
		return () => {
			window.removeEventListener('resize', _resizeListener);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [config]);

	useEffect(() => {
		return history.listen(() => {
		  if (history.action === 'POP') {
			  if (identityStatusRef.current !== 'allowed') {
				  return;
			  }
			  const uid = getParams('uid');
			  if(uid === null) {
				  // this covers the case when user clicks on browser back button 
				  // at the landing page.
				  // https://sureify.atlassian.net/browse/NAT-1573
				window.history.back()
			  } else {
				store.dispatch(
					getNextQuestionSet('/questions', { uid }, { reload: true })
				)
			  }
			}
		})
	    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [history]);
	  
	return (
		<Provider store={ store }>
			<FullStoryInitialization />
			<Helmet>
				<title>
					{get(config, 'global.appTitle', 'Acquire')}
				</title>
			</Helmet>
			<HealthCheck>
				<IdentityCheck
					identityCheckEnable={ identityCheckEnable }
					identityCheckTimeoutMs={ identityCheckTimeoutMs }
					onStatusChange={ handleIdentityStatus }
				>
					<ThemeProvider theme={ { config } }>
						<GlobalStyle fontFamily={ fontFamily } />
						<Layout />
					</ThemeProvider>
				</IdentityCheck>
			</HealthCheck>
		</Provider>
	);
};

Main.propTypes = {
	config: PropTypes.any.isRequired,
};
export default withRouter(Main);
